import React, { createContext, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { type ControllableElement } from '@bynder-studio/render-core';
import useEditor from '../hooks/useEditor';

type ContextData = {
    selectedElement: ControllableElement;
    selectedElements: ControllableElement[];
    topLevelSelectedElements: ControllableElement[];
    selectedElementDeferred: ControllableElement;
    selectedElementsDeferred: ControllableElement[];
    topLevelSelectedElementsDeferred: ControllableElement[];
};

export const EditorSelectedElementContext = createContext({} as ContextData);

export function EditorSelectedElementProvider({ children }) {
    const { manipulationRenderer, creativeModel } = useEditor();
    const [selectedElement, setSelectedElement] = useState(manipulationRenderer?.getSelectedElement());
    const [selectedElements, setSelectedElements] = useState(manipulationRenderer?.getSelectedElements());
    const [topLevelSelectedElements, setTopLevelSelectedElements] = useState(
        manipulationRenderer?.getTopLevelSelectedElements(),
    );

    const selectedElementDeferred = useDeferredValue(selectedElement);
    const selectedElementsDeferred = useDeferredValue(selectedElements);
    const topLevelSelectedElementsDeferred = useDeferredValue(topLevelSelectedElements);

    const value = useMemo(
        () => ({
            selectedElement,
            selectedElements,
            topLevelSelectedElements,
            selectedElementDeferred,
            selectedElementsDeferred,
            topLevelSelectedElementsDeferred,
        }),
        [
            selectedElement,
            selectedElements,
            topLevelSelectedElements,
            selectedElementDeferred,
            selectedElementsDeferred,
            topLevelSelectedElementsDeferred,
        ],
    );

    useEffect(() => {
        if (!manipulationRenderer) {
            return;
        }

        const onElementSelected = () => {
            setSelectedElement(manipulationRenderer.getSelectedElement());
            setSelectedElements(manipulationRenderer.getSelectedElements());
            setTopLevelSelectedElements(manipulationRenderer.getTopLevelSelectedElements());
        };

        manipulationRenderer.eventEmitter.on('elementSelected', onElementSelected);

        return () => {
            manipulationRenderer.eventEmitter.off('elementSelected', onElementSelected);
        };
    }, [manipulationRenderer]);

    useEffect(() => {
        const handleUndoRedoSnapshotRestore = () => {
            const selectedEl = manipulationRenderer?.getSelectedElement();
            const selectedEls = manipulationRenderer?.getSelectedElements();
            const topLevelSelectedEls = manipulationRenderer?.getTopLevelSelectedElements();

            setSelectedElement(selectedEl ? creativeModel.getElementById(selectedEl.id) : null);

            setSelectedElements(selectedEls.map((el) => creativeModel.getElementById(el.id)).filter((el) => !!el));

            setTopLevelSelectedElements(
                topLevelSelectedEls.map((el) => creativeModel.getElementById(el.id)).filter((el) => !!el),
            );
        };

        creativeModel.getEventEmitter().on('undoRedoSnapshotRestore', handleUndoRedoSnapshotRestore);

        return () => {
            creativeModel.getEventEmitter().off('undoRedoSnapshotRestore', handleUndoRedoSnapshotRestore);
        };
    }, [creativeModel, manipulationRenderer]);

    return <EditorSelectedElementContext.Provider value={value}>{children}</EditorSelectedElementContext.Provider>;
}
